import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/pages.css';
import { db } from '../config/firebase';
import { collection, getDocs } from 'firebase/firestore';

const ViewAllTutorials = () => {
  const navigate = useNavigate();
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [tutorialStatuses, setTutorialStatuses] = useState({});

  // Tutorials with Google Drive file IDs
  const mockTutorials = useMemo(() => ([
    { id: 1, title: 'Tutorial 1', duration: '20 mins', fileId: '16NYN4YGqodC1-A-yE1I-cj7kQnyWF1gL' },
    { id: 2, title: 'Tutorial 2', duration: '22 mins', fileId: '1-D-zdDEF9_F4--iQCt9alTNoK99CuMSP' },
    { id: 3, title: 'Tutorial 3', duration: '25 mins', fileId: '1McZ4oy8sUuHhhACqmQBXS6zCxTUq4Tjd' },
    { id: 4, title: 'Tutorial 4', duration: '21 mins', fileId: '1s4dE0FZlmCDEaQQgh0HkLDZPoA6Tmfd-' },
    { id: 5, title: 'Tutorial 5', duration: '23 mins', fileId: '11zRMD-QXFGMIe5G8HLiIApLpIFekTjRP' },
    { id: 6, title: 'Tutorial 6', duration: '24 mins', fileId: '1PoUO-3zJ5E0nBg9kGP1gONWgK-DkWDec' },
    { id: 7, title: 'Tutorial 7', duration: '20 mins', fileId: '1FZEWobP-fsPOb1bKSi73ETHa7iQTXHUh' },
    { id: 8, title: 'Tutorial 8', duration: '26 mins', fileId: '1JKXp-A5MO2TRdHYpFCGySydv7RicEsg0' },
    { id: 9, title: 'Tutorial 9', duration: '28 mins', fileId: '1l2KxiGWf7MOQnfSaUXEHiGqT89jTRCZs' }
  ]), []);

  useEffect(() => {
    // Load tutorial statuses from Firestore
    const loadTutorialStatuses = async () => {
      try {
        const instructorsRef = collection(db, 'instructors');
        const queryResult = await getDocs(instructorsRef);
        
        let allStatuses = {};
        for (const instructorDoc of queryResult.docs) {
          try {
            const statusCollectionRef = collection(db, 'instructors', instructorDoc.id, 'tutorialStatuses');
            const statusDocs = await getDocs(statusCollectionRef);
            
            for (const statusDoc of statusDocs.docs) {
              const statuses = statusDoc.data();
              // statuses contains tutorial_1: 'released', tutorial_2: 'closed', etc.
              Object.assign(allStatuses, statuses);
            }
          } catch (error) {
            console.log('Could not load tutorial statuses for instructor:', instructorDoc.id);
          }
        }
        
        console.log('[ViewAllTutorials] Loaded tutorial statuses from Firestore:', allStatuses);
        setTutorialStatuses(allStatuses);
        
        // Set first released tutorial as default
        const availableTutorials = mockTutorials.filter(t => allStatuses[`tutorial_${t.id}`] === 'released');
        if (availableTutorials.length > 0 && !selectedTutorial) {
          setSelectedTutorial(availableTutorials[0]);
        }
      } catch (error) {
        console.error('Error loading tutorial statuses:', error);
      }
    };
    
    loadTutorialStatuses();
  }, [mockTutorials, selectedTutorial]);

  const releasedTutorials = mockTutorials.filter(t => tutorialStatuses[`tutorial_${t.id}`] === 'released');

  const current = selectedTutorial || releasedTutorials[0];

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', padding: '2rem 0' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '3rem' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: '40px',
              height: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              background: '#1a1a1a',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 300ms ease',
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#2d2d2d';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#1a1a1a';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1 style={{ fontSize: '2.5rem', margin: '0 0 0.5rem 0', fontWeight: 700, color: '#1a1a1a', letterSpacing: '-1px' }}>
              <i className="fas fa-graduation-cap" style={{ marginRight: '1rem' }}></i>
              View Tutorials
            </h1>
            <p style={{ color: '#666', margin: 0, fontSize: '1rem' }}>
              Available tutorial PDF content
            </p>
          </div>
        </div>

        {releasedTutorials.length === 0 ? (
          <div style={{
            background: '#f9f9f9',
            border: '2px dashed #e8e8e8',
            borderRadius: '8px',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '1.1rem', color: '#666', margin: 0 }}>
              No tutorials available yet. Check back soon!
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '2rem' }}>
            {/* Tutorial List Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a1a', margin: '0 0 1rem 0' }}>
                Available Tutorials ({releasedTutorials.length})
              </h2>
              {releasedTutorials.map((tutorial) => (
                <button
                  key={tutorial.id}
                  onClick={() => setSelectedTutorial(tutorial)}
                  style={{
                    background: current?.id === tutorial.id ? '#1a1a1a' : '#f9f9f9',
                    color: current?.id === tutorial.id ? 'white' : '#1a1a1a',
                    border: `2px solid ${current?.id === tutorial.id ? '#1a1a1a' : '#e8e8e8'}`,
                    borderRadius: '6px',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 300ms ease',
                    textAlign: 'left',
                    fontWeight: current?.id === tutorial.id ? 600 : 500,
                    fontSize: '0.95rem'
                  }}
                  onMouseOver={(e) => {
                    if (current?.id !== tutorial.id) {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (current?.id !== tutorial.id) {
                      e.currentTarget.style.borderColor = '#e8e8e8';
                      e.currentTarget.style.backgroundColor = '#f9f9f9';
                    }
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>
                    {tutorial.title}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    <i className="fas fa-clock"></i> {tutorial.duration}
                  </div>
                </button>
              ))}
            </div>

            {/* PDF Viewer */}
            {current && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Tutorial Info Card */}
                <div style={{
                  background: '#f9f9f9',
                  border: '2px solid #e8e8e8',
                  borderRadius: '8px',
                  padding: '1.5rem'
                }}>
                  <h2 style={{ margin: '0 0 0.75rem 0', color: '#1a1a1a', fontSize: '1.4rem', fontWeight: 700 }}>
                    <i className="fas fa-file-pdf" style={{ marginRight: '0.75rem', color: '#7A5107' }}></i>
                    {current.title}
                  </h2>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
                    <i className="fas fa-clock" style={{ marginRight: '0.5rem' }}></i>Duration: {current.duration}
                  </p>
                </div>
                
                {/* Embedded PDF Preview */}
                <div style={{
                  border: '2px solid #e8e8e8',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#f9f9f9'
                }}>
                  <iframe
                    src={`https://drive.google.com/file/d/${current.fileId}/preview`}
                    style={{
                      width: '100%',
                      height: '700px',
                      border: 'none'
                    }}
                    allow="autoplay"
                    title={current.title}
                  ></iframe>
                </div>

                {/* Download Link */}
                <a
                  href={`https://drive.google.com/file/d/${current.fileId}/view?usp=sharing`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    background: '#1a1a1a',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    transition: 'all 300ms ease',
                    alignSelf: 'flex-start'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#7A5107';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#1a1a1a';
                  }}
                >
                  <i className="fas fa-external-link-alt"></i>
                  Open in Google Drive
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAllTutorials;
